/**
 * Нормализация извлечённого текста (PDF/OCR) к формату импорта контура:
 * одна пара десятичных координат на строку.
 *
 * Поддерживает:
 * - DMS: 043° 30' 42.1800''
 * - таблицу «Широта / Долгота» (пары в строке или чередующийся список)
 * - уже готовые десятичные строки / GeoJSON — без изменений
 */

import {
  parseGeometryCoordsFromText,
  type CoordinateOrder,
} from "@/lib/geometry-coords-import";

/** DMS: градусы °/º, минуты '/′, секунды с одним или двумя штрихами / ″ / ". */
const DMS_TOKEN_RE =
  /(\d{1,3})\s*[°º]\s*(\d{1,2})\s*['′]\s*(\d{1,2}(?:[.,]\d+)?)\s*(?:['′]{1,2}|["″]+)/g;

export type DocumentCoordsNormalizeResult =
  | {
      ok: true;
      text: string;
      pointCount: number;
      convertedFromDms: boolean;
    }
  | { ok: false; error: string; text: string };

function dmsToDecimal(deg: number, min: number, sec: number): number {
  const sign = deg < 0 ? -1 : 1;
  return sign * (Math.abs(deg) + min / 60 + sec / 3600);
}

function parseDmsToken(match: RegExpMatchArray): number | null {
  const deg = Number(match[1]);
  const min = Number(match[2]);
  const sec = Number(String(match[3]).replace(",", "."));
  if ([deg, min, sec].some((n) => Number.isNaN(n))) return null;
  if (min < 0 || min >= 60 || sec < 0 || sec >= 60) return null;
  if (Math.abs(deg) > 180) return null;
  return dmsToDecimal(deg, min, sec);
}

function extractDmsValues(raw: string): number[] {
  const values: number[] = [];
  const re = new RegExp(DMS_TOKEN_RE.source, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    const v = parseDmsToken(m);
    if (v != null) values.push(v);
  }
  return values;
}

/**
 * Порядок в исходной таблице по заголовкам.
 * «Широта … Долгота» → latlon; «Долгота … Широта» → lonlat.
 * По умолчанию для русскоязычных ведомостей — latlon (как в образце).
 */
function detectSourcePairOrder(raw: string): CoordinateOrder {
  const lower = raw.toLowerCase();
  const latIdx = lower.search(/широт/);
  const lonIdx = lower.search(/долгот/);
  if (latIdx >= 0 && lonIdx >= 0) {
    return latIdx <= lonIdx ? "latlon" : "lonlat";
  }
  return "latlon";
}

function formatDecimal(n: number): string {
  const s = n.toFixed(8).replace(/\.?0+$/, "");
  return s === "-0" ? "0" : s;
}

function formatOutputPair(lon: number, lat: number, outputOrder: CoordinateOrder): string {
  return outputOrder === "latlon"
    ? `${formatDecimal(lat)} ${formatDecimal(lon)}`
    : `${formatDecimal(lon)} ${formatDecimal(lat)}`;
}

function validateLonLat(lon: number, lat: number): boolean {
  return lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90;
}

function buildPairsFromDmsSequence(
  values: number[],
  sourceOrder: CoordinateOrder,
): Array<{ lon: number; lat: number }> | null {
  if (values.length < 2 || values.length % 2 !== 0) return null;

  const pairs: Array<{ lon: number; lat: number }> = [];
  for (let i = 0; i < values.length; i += 2) {
    const a = values[i];
    const b = values[i + 1];
    const lon = sourceOrder === "latlon" ? b : a;
    const lat = sourceOrder === "latlon" ? a : b;
    if (!validateLonLat(lon, lat)) return null;
    pairs.push({ lon, lat });
  }
  return pairs;
}

function countImportPoints(feature: GeoJSON.Feature): number {
  const g = feature.geometry;
  if (!g) return 0;
  if (g.type === "Point") return 1;
  if (g.type === "LineString") return g.coordinates.length;
  if (g.type === "Polygon") {
    const ring = g.coordinates[0] ?? [];
    return Math.max(0, ring.length > 1 ? ring.length - 1 : ring.length);
  }
  return 0;
}

/**
 * Нормализует текст распознавания под импорт контура.
 * @param outputOrder — порядок в выходном тексте (как в UI РД: lonlat по умолчанию).
 */
export function normalizeDocumentCoordsForImport(
  raw: string,
  options?: { outputOrder?: CoordinateOrder },
): DocumentCoordsNormalizeResult {
  const outputOrder = options?.outputOrder ?? "lonlat";
  const trimmed = (raw || "").trim();
  if (!trimmed) {
    return { ok: false, error: "Пустой текст распознавания", text: raw };
  }

  const existing = parseGeometryCoordsFromText(trimmed, { coordinateOrder: outputOrder });
  if (existing.ok) {
    return {
      ok: true,
      text: trimmed,
      pointCount: countImportPoints(existing.feature),
      convertedFromDms: false,
    };
  }

  const dmsValues = extractDmsValues(trimmed);
  if (dmsValues.length >= 2) {
    const sourceOrder = detectSourcePairOrder(trimmed);
    const pairs = buildPairsFromDmsSequence(dmsValues, sourceOrder);
    if (!pairs) {
      return {
        ok: false,
        error:
          dmsValues.length % 2 !== 0
            ? `Нечётное число DMS-координат (${dmsValues.length}): не удалось собрать пары широта/долгота.`
            : "Не удалось собрать валидные пары координат из DMS.",
        text: trimmed,
      };
    }

    const text = pairs.map((p) => formatOutputPair(p.lon, p.lat, outputOrder)).join("\n");
    const check = parseGeometryCoordsFromText(text, { coordinateOrder: outputOrder });
    if (!check.ok) {
      return { ok: false, error: check.error, text: trimmed };
    }
    return { ok: true, text, pointCount: pairs.length, convertedFromDms: true };
  }

  return {
    ok: false,
    error: existing.error,
    text: trimmed,
  };
}
